import { Fragment, createContext, useContext, useId, useRef } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { X } from "lucide-react";
import { useBackdropDismissGuard, useModalAccessibility } from "./overlay";
import { cn } from "@/lib/utils";
import Button from "./Button";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  closeLabel: string;
  title?: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  showCloseButton?: boolean;
  /** When false, title/description still wire aria-* but are not rendered in the header. */
  showHeader?: boolean;
  priority?: "default" | "high";
  role?: "dialog" | "alertdialog";
  dismissOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  mobileFullScreen?: boolean;
}

/** True when ModalFooter is rendered through Modal's `footer` slot (outside the padded body). */
const ModalFooterSlotContext = createContext(false);

const sizeClasses = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
  full: "max-w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)]",
};

export function Modal({
  isOpen,
  onClose,
  closeLabel,
  title,
  description,
  children,
  footer,
  size = "md",
  showCloseButton = true,
  showHeader = true,
  priority = "default",
  role = "dialog",
  dismissOnBackdrop = true,
  closeOnEscape,
  mobileFullScreen = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const dialogId = useId();
  const titleId = `${dialogId}-title`;
  const descriptionId = `${dialogId}-description`;
  const guardBackdropDismiss = useBackdropDismissGuard(isOpen);
  const resolvedCloseOnEscape = closeOnEscape ?? dismissOnBackdrop;
  const zClass = priority === "high" ? "z-[60]" : "z-50";

  useModalAccessibility({
    isOpen,
    onClose,
    closeOnEscape: resolvedCloseOnEscape,
    dialogRef,
  });

  if (!isOpen) return null;

  return createPortal(
    <Fragment>
      <div
        className={cn(
          "fixed inset-0 animate-fade-in bg-surface-950/40 backdrop-blur-[2px] dark:bg-black/70",
          zClass,
        )}
        onClick={() => {
          if (dismissOnBackdrop) {
            guardBackdropDismiss(onClose);
          }
        }}
        aria-hidden="true"
      />
      <div
        className={cn(
          "fixed inset-0 flex justify-center",
          mobileFullScreen
            ? "items-stretch p-0 sm:items-center sm:p-4"
            : "items-end p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4",
          zClass,
        )}
      >
        <div
          ref={dialogRef}
          id={dialogId}
          tabIndex={-1}
          className={cn(
            "relative w-full min-w-0 animate-scale-in border border-surface-200 bg-surface-0 shadow-popover",
            "flex flex-col overflow-hidden outline-none",
            mobileFullScreen
              ? "h-[100dvh] max-h-[100dvh] rounded-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] sm:h-auto sm:max-h-[min(90dvh,calc(100dvh-1.5rem))] sm:rounded-xl sm:pt-0 sm:pb-0"
              : "max-h-[min(90dvh,calc(100dvh-1.5rem))] rounded-xl",
            sizeClasses[size],
          )}
          role={role}
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-describedby={description ? descriptionId : undefined}
          onClick={(e) => e.stopPropagation()}
        >
          {(showHeader && (title || showCloseButton)) && (
            <div className="flex shrink-0 items-start justify-between gap-3 border-b border-surface-200/70 px-4 py-4 sm:px-6">
              <div className="min-w-0 flex-1">
                {title && (
                  <h2
                    id={titleId}
                    className="text-balance text-base font-semibold tracking-tight text-surface-900 sm:text-lg"
                  >
                    {title}
                  </h2>
                )}
                {description && (
                  <p
                    id={descriptionId}
                    className="mt-1 text-pretty text-sm text-surface-500"
                  >
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={onClose}
                  className="-mr-1 shrink-0 text-surface-400 hover:text-surface-600 sm:-mr-2"
                  aria-label={closeLabel}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          {!showHeader && title ? (
            <h2 id={titleId} className="sr-only">
              {title}
            </h2>
          ) : null}
          {!showHeader && description ? (
            <p id={descriptionId} className="sr-only">
              {description}
            </p>
          ) : null}
          {children ? (
            <div
              className={cn(
                "min-h-0 flex-1 px-4 py-4 sm:px-6 sm:py-5",
                mobileFullScreen
                  ? "flex flex-col overflow-hidden"
                  : "overflow-y-auto overscroll-contain",
              )}
            >
              {children}
            </div>
          ) : null}
          {footer ? (
            <ModalFooterSlotContext.Provider value>
              <div className="shrink-0">{footer}</div>
            </ModalFooterSlotContext.Provider>
          ) : null}
        </div>
      </div>
    </Fragment>,
    document.body,
  );
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
}

export type { ModalProps, ModalFooterProps };

export function ModalFooter({ children, className }: ModalFooterProps) {
  const inFooterSlot = useContext(ModalFooterSlotContext);
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 border-t border-surface-200/70 bg-surface-50/70 px-4 py-3",
        "sm:flex-row sm:items-center sm:justify-end sm:gap-3 sm:px-6",
        // Inside the body: bleed to the dialog edges so it reads as the same footer bar.
        !inFooterSlot && "-mx-4 -mb-4 mt-5 sm:-mx-6 sm:-mb-5",
        "[&_button]:w-full sm:[&_button]:w-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}
